import React from "react";

import { strings } from "@perceptron/constants";
import { formatString } from "@perceptron/utils/string";
import { Icon, IconName } from "@perceptron/ui/components/icon";
import { Tooltip, TooltipContent, TooltipTrigger } from "@perceptron/ui/components/tooltip";
import { cn } from "@perceptron/utils/cn";
import { animated, SpringValue } from "react-spring";
import { decode } from "he";

import { POINT_BOX_COLOR_BASE, SEGMENT_COLOR_BASE, TEXT_COLOR, TRACK_COLOR_BASE } from "./constants";
import { TAnimatedStyle, TRenderableRow } from "./types";
import { useTimelineDataStore, useTimelineStore } from "./store/timeline-context";
import { createFocusIndex, type AnnotationFocusIndex } from "./utils/focus";

const INDENT_PX = 12;

type SpatialKind = "point_box" | "point" | "polygon";

// Keep in sync with SelectOutputFormat/Content.tsx (POINT → crosshair, BOX → box-icon, POLYGON → polygon, CLIP → camera).
const SPATIAL_ICON: Record<SpatialKind, IconName> = {
  point_box: "box-icon",
  point: "crosshair",
  polygon: "polygon",
};

// Mirror Segment.tsx's per-type color table so icons match their cluster bars.
const SPATIAL_COLOR: Record<SpatialKind, string> = {
  point_box: POINT_BOX_COLOR_BASE,
  point: POINT_BOX_COLOR_BASE,
  polygon: POINT_BOX_COLOR_BASE,
};

function RowIcon({ row }: { row: TRenderableRow }) {
  if (row.kind === "track") {
    const kind = row.data.waypointKind ?? "point_box";
    return <Icon className="size-4" name={SPATIAL_ICON[kind]} style={{ color: TRACK_COLOR_BASE }} />;
  }

  if (row.kind === "bare-segment") {
    if (row.data.type === "clip") {
      return <Icon className="size-4" name="camera" style={{ color: SEGMENT_COLOR_BASE }} />;
    }
    return (
      <Icon className="size-4" name={SPATIAL_ICON[row.data.type]} style={{ color: SPATIAL_COLOR[row.data.type] }} />
    );
  }

  return null;
}

const LabelRow = React.memo(
  ({
    row,
    style,
    onCollapseToggle,
    focusIndex,
  }: {
    row: TRenderableRow;
    style: TAnimatedStyle;
    onCollapseToggle: (collectionId: string) => void;
    focusIndex: AnnotationFocusIndex;
  }) => {
    const isCollection = row.kind === "collection-header";
    const canFocus = focusIndex.has(row.data.id);
    const LabelArea = isCollection ? "button" : "div";
    // Each row subscribes to booleans about itself, so a hover or selection
    // re-renders only the rows whose styling actually changes.
    const selected = useTimelineStore((state) => state.selectedSegmentId === row.data.id);
    const hovered = useTimelineStore((state) => state.hoveredTimelineRow?.id === row.data.id);
    const related = useTimelineStore((state) => {
      // Label hover previews another scope; canvas hover leaves locked label styling intact.
      const hoveredLabelId = state.hoveredTimelineRow?.area === "label" ? state.hoveredTimelineRow.id : null;
      const labelFocusId = [hoveredLabelId, state.selectedSegmentId].find((id) => id && focusIndex.has(id));
      return (
        !canFocus ||
        !labelFocusId ||
        !!focusIndex.get(labelFocusId)?.ids.has(row.data.id) ||
        !!focusIndex.get(row.data.id)?.ids.has(labelFocusId)
      );
    });
    const toggleSegment = useTimelineStore((state) => state.toggleSegment);
    const setHoveredRow = useTimelineStore((state) => state.setHoveredTimelineRow);
    const clearHoveredRow = useTimelineStore((state) => state.clearHoveredTimelineRow);
    const [tooltipOpen, setTooltipOpen] = React.useState(false);
    const label = decode(row.label);
    const lockLabel = formatString(
      selected ? strings.videoPlayer.timelineFocus.unlock : strings.videoPlayer.timelineFocus.lock,
      label,
    );
    const preview = () => {
      if (canFocus) setHoveredRow(row.data.id, "label");
    };
    const leave = () => clearHoveredRow(row.data.id, "label");
    React.useEffect(() => () => clearHoveredRow(row.data.id, "label"), [row.data.id, clearHoveredRow]);

    return (
      <animated.div
        className="absolute left-0 right-0 flex items-center overflow-hidden border-t border-border pr-2 legal"
        data-row-kind={row.kind}
        data-timeline-row={row.data.id}
        style={{ ...style, color: TEXT_COLOR, width: "100%" }}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) leave();
        }}
        onFocus={preview}
        onMouseEnter={preview}
        onMouseLeave={leave}
      >
        {canFocus ? (
          <button
            aria-label={lockLabel}
            aria-pressed={selected}
            className={cn(
              "flex size-6 shrink-0 items-center justify-center rounded-sm transition-opacity focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-brand-blue",
              selected
                ? "opacity-100"
                : hovered
                  ? "opacity-50 hover:opacity-100"
                  : "opacity-0 [@media(hover:none)]:opacity-50",
            )}
            data-focus-lock={row.data.id}
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              toggleSegment(row.data.id);
              // Pointer activation returns Space to playback; keyboard activation retains button focus.
              if (event.detail > 0) event.currentTarget.blur();
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") event.stopPropagation();
            }}
          >
            <Icon aria-hidden="true" className="size-3" name={selected ? "lock" : "lock-open"} />
          </button>
        ) : (
          <span aria-hidden="true" className="size-6 shrink-0" />
        )}
        <LabelArea
          aria-expanded={isCollection ? !row.isCollapsed : undefined}
          aria-label={
            isCollection
              ? formatString(
                  row.isCollapsed
                    ? strings.videoPlayer.timelineFocus.expand
                    : strings.videoPlayer.timelineFocus.collapse,
                  label,
                )
              : undefined
          }
          className={cn(
            "flex h-full min-w-0 flex-1 items-center gap-1.5 rounded-sm px-1 text-left transition-opacity focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-brand-blue",
            !related && "opacity-50",
          )}
          data-timeline-label-area={row.data.id}
          style={{ marginLeft: row.depth * INDENT_PX }}
          type={isCollection ? "button" : undefined}
          onBlur={() => setTooltipOpen(false)}
          onClick={(event) => {
            event.stopPropagation();
            if (isCollection) onCollapseToggle(row.data.id);
          }}
          onFocus={() => setTooltipOpen(true)}
          onKeyDown={(event) => {
            if (isCollection && (event.key === "Enter" || event.key === " ")) event.stopPropagation();
          }}
        >
          {!isCollection && <RowIcon row={row} />}
          {/* Anchor to the text, not the full-width row hit area. */}
          <Tooltip delayDuration={0} disableHoverableContent open={tooltipOpen} onOpenChange={setTooltipOpen}>
            <TooltipTrigger asChild>
              <span className="truncate legal" data-timeline-label={row.data.id}>
                {label}
              </span>
            </TooltipTrigger>
            <TooltipContent
              align="start"
              className="dark pointer-events-none max-w-[min(18rem,var(--radix-tooltip-content-available-width))] whitespace-normal break-words border border-border px-2.5 py-1.5 text-left shadow-none legal [text-wrap:wrap] data-[state=closed]:!animate-none"
              collisionPadding={12}
              hideWhenDetached
              side="top"
              sideOffset={6}
              theme="popover"
            >
              <p>{label}</p>
            </TooltipContent>
          </Tooltip>
          {isCollection && (
            <Icon
              aria-hidden="true"
              className={cn("size-3 shrink-0 transition-transform duration-300 ease-in-out", {
                "-rotate-90": row.isCollapsed,
              })}
              name="chevron-down"
            />
          )}
        </LabelArea>
      </animated.div>
    );
  },
);

LabelRow.displayName = "LabelRow";

type Props = {
  transitions: (
    render: (style: TAnimatedStyle, item: TRenderableRow | null) => React.ReactNode | null,
  ) => React.ReactNode;
  animatedTotalHeight: SpringValue<number>;
  onCollapseToggle: (collectionId: string) => void;
};

export const TrackLabelPane: React.FC<Props> = React.memo(({ transitions, animatedTotalHeight, onCollapseToggle }) => {
  const items = useTimelineDataStore((state) => state.items);
  const focusIndex = React.useMemo(() => createFocusIndex(items), [items]);
  return (
    <animated.div className="relative" style={{ height: animatedTotalHeight }}>
      {transitions((style: TAnimatedStyle, row: TRenderableRow | null) => {
        if (!row) return null; // Guard against leave transitions
        return (
          <LabelRow focusIndex={focusIndex} key={row.id} row={row} style={style} onCollapseToggle={onCollapseToggle} />
        );
      })}
    </animated.div>
  );
});

TrackLabelPane.displayName = "TrackLabelPane";
